/**
 * AiAssistedChip — Slice 16b.
 *
 * Small inline chip rendered next to audit-log rows and version-history
 * rows that carry AI provenance. Styled in indigo (from token layer) to
 * signal "produced by AI" distinctly from the terracotta primary-action
 * palette and the sage/ochre/clay status palette.
 *
 * Token-first: no hardcoded hex values. Indigo tokens fall back to
 * the base paper/ink tokens so the chip degrades gracefully if the
 * indigo tokens are not yet wired.
 */

export type ProseOrigin = 'ai' | 'ai_edited' | 'human'

export interface AiAssistedChipProps {
  modelId: string
  promptVersion: string
  proseOrigin?: ProseOrigin | null | undefined
  similarityRatio?: number | null | undefined
  /** Optional click handler — e.g. to navigate to the suggestion audit row.
   *  When absent the chip is non-interactive (no cursor:pointer, no role). */
  onLinkClick?: (() => void) | undefined
}

const PROSE_ORIGIN_LABELS: Record<ProseOrigin, string> = {
  ai: 'AI draft accepted',
  ai_edited: 'AI draft edited',
  human: 'Human-written, AI consulted',
}

function buildTooltip(
  modelId: string,
  promptVersion: string,
  proseOrigin: ProseOrigin | null | undefined,
  similarityRatio: number | null | undefined,
): string {
  const lines: string[] = [
    'AI-assisted',
    `Model: ${modelId}`,
    `Prompt: ${promptVersion}`,
  ]
  if (proseOrigin != null) {
    lines.push(`Prose origin: ${PROSE_ORIGIN_LABELS[proseOrigin]}`)
  }
  if (similarityRatio != null) {
    lines.push(`Similarity: ${Math.round(similarityRatio * 100)}%`)
  }
  return lines.join('\n')
}

export function AiAssistedChip({
  modelId,
  promptVersion,
  proseOrigin,
  similarityRatio,
  onLinkClick,
}: Readonly<AiAssistedChipProps>): JSX.Element {
  const tooltip = buildTooltip(modelId, promptVersion, proseOrigin, similarityRatio)
  const interactive = onLinkClick != null

  return (
    <span
      data-testid="ai-assisted-chip"
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      title={tooltip}
      onClick={interactive ? onLinkClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onLinkClick()
              }
            }
          : undefined
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontFamily: 'var(--font-mono, monospace)',
        lineHeight: 1.4,
        background: 'var(--ilmu-bg, var(--surface-2))',
        border: '1px solid var(--ilmu-line, var(--border))',
        color: 'var(--ilmu-fg, var(--text-2))',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      AI-assisted
    </span>
  )
}
